import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ArrowRight, Send, Building2 } from 'lucide-react';
import { Seo } from '../components/seo/Seo';
import { Footer } from '../components/brand/Footer';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /partners/:slug/refer — Blueprint Decision #20.
 *
 * Public co-branded discharge-referral form. No auth required. The slug
 * resolves a partner doc; the partner's branding is applied to the page
 * (hero message + accent color); the submitted referral is queued in the
 * matching pipeline with provenance = "partner_portal".
 */
export default function PartnerIntake() {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState(null);

  const [form, setForm] = useState({
    case_manager_name: '',
    case_manager_email: '',
    patient_initials: '',
    primary_diagnosis: '',
    discharge_date: '',
    care_type: 'skilled_nursing',
    city: '',
    state: '',
    priority: 'routine',
    notes: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/partners/${slug}/intake-config`, { timeout: 12000 });
        if (!cancelled) setConfig(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 404 ? 'partner_not_found' : 'unknown');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/partners/${slug}/referrals`, form);
      setSuccess(data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center" data-testid="partner-intake-loading">
        <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (err === 'partner_not_found' || !config) {
    return (
      <div className="min-h-screen bg-cn-bg" data-testid="partner-intake-not-found">
        <div className="cn-container py-20 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h1 className="font-display font-bold text-2xl text-cn-text mb-2">Partner portal not available</h1>
          <p className="text-sm text-cn-text-muted mb-6">
            The link you followed doesn&rsquo;t match an active partner.
            If you believe this is an error, contact your CaraLoom account manager.
          </p>
          <Link to="/" className="cn-btn cn-btn-primary">Back to CaraLoom</Link>
        </div>
      </div>
    );
  }

  const accent = config.branding?.color_accent || '#0E3D3A';

  if (success) {
    return (
      <div className="min-h-screen bg-cn-bg" data-testid="partner-intake-success">
        <header className="border-b border-cn-border" style={{ background: `${accent}0d` }}>
          <div className="cn-container py-5 flex items-center gap-3">
            <CaraLoomLogo size={28} />
            <span className="text-cn-text-muted text-sm">×</span>
            <Building2 className="w-5 h-5" style={{ color: accent }} />
            <span className="font-semibold text-cn-text">{config.branding?.facility_name}</span>
          </div>
        </header>
        <section className="cn-container py-20 max-w-2xl text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-5" style={{ color: accent }} />
          <h1 className="font-display font-bold text-3xl text-cn-text mb-3">Referral submitted</h1>
          <p className="text-cn-text-muted mb-6 leading-relaxed">{success.next_step}</p>
          <div className="cn-card p-5 inline-flex flex-col text-left gap-1">
            <span className="text-xs text-cn-text-muted">Referral ID</span>
            <span className="font-mono text-sm text-cn-text">{success.referral_id}</span>
          </div>
          <div className="mt-8">
            <Button
              onClick={() => { setSuccess(null); setForm({ ...form, patient_initials: '', primary_diagnosis: '', discharge_date: '', notes: '' }); }}
              className="cn-btn cn-btn-primary"
              data-testid="partner-submit-another"
            >
              Submit another referral <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="partner-intake-page">
      <Seo
        title={`Refer a patient · ${config.branding?.facility_name} | CaraLoom`}
        description={`Partner discharge-referral portal for ${config.branding?.facility_name}.`}
        path={`/partners/${slug}/refer`}
        keywords={['discharge referral', 'partner portal', config.branding?.facility_name]}
      />

      {/* CO-BRANDED HEADER */}
      <header className="border-b border-cn-border" style={{ background: `${accent}0d` }}>
        <div className="cn-container py-5 flex items-center gap-3" data-testid="partner-cobrand-header">
          <CaraLoomLogo size={28} />
          <span className="text-cn-text-muted text-sm">×</span>
          <Building2 className="w-5 h-5" style={{ color: accent }} />
          <span className="font-semibold text-cn-text" data-testid="partner-facility-name">{config.branding?.facility_name}</span>
        </div>
      </header>

      {/* HERO */}
      <section className="cn-section pt-12 pb-8" style={{ background: `${accent}05` }}>
        <div className="cn-container max-w-3xl">
          <h1
            className="font-display font-bold text-3xl sm:text-4xl text-cn-text leading-tight"
            data-testid="partner-headline"
          >
            Refer a patient
          </h1>
          {config.branding?.hero_message && (
            <p className="mt-3 text-base text-cn-text-muted leading-relaxed" data-testid="partner-hero-message">
              {config.branding.hero_message}
            </p>
          )}
        </div>
      </section>

      {/* FORM */}
      <section className="cn-section py-10">
        <div className="cn-container max-w-3xl">
          <form onSubmit={submit} className="cn-card p-6 space-y-4" data-testid="partner-intake-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-cm-name">Your name</label>
                <input id="partner-cm-name" className="cn-input w-full" value={form.case_manager_name} onChange={update('case_manager_name')} required data-testid="partner-cm-name" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-cm-email">Your email</label>
                <input id="partner-cm-email" type="email" className="cn-input w-full" value={form.case_manager_email} onChange={update('case_manager_email')} required data-testid="partner-cm-email" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-initials">Patient initials</label>
                <input id="partner-initials" className="cn-input w-full" placeholder="J.D." value={form.patient_initials} onChange={update('patient_initials')} required maxLength={10} data-testid="partner-initials" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-dx">Primary diagnosis</label>
                <input id="partner-dx" className="cn-input w-full" placeholder="e.g. Heart failure (I50.9)" value={form.primary_diagnosis} onChange={update('primary_diagnosis')} required data-testid="partner-diagnosis" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-discharge-date">Discharge date</label>
                <input id="partner-discharge-date" type="date" className="cn-input w-full" value={form.discharge_date} onChange={update('discharge_date')} required data-testid="partner-discharge-date" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-care-type">Care type</label>
                <select id="partner-care-type" className="cn-input w-full" value={form.care_type} onChange={update('care_type')} data-testid="partner-care-type">
                  {(config.form_schema?.care_types || ['skilled_nursing']).map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-city">City</label>
                <input id="partner-city" className="cn-input w-full" value={form.city} onChange={update('city')} required data-testid="partner-city" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-state">State</label>
                <input id="partner-state" className="cn-input w-full" maxLength={2} value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))} required data-testid="partner-state" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-priority">Priority</label>
              <div className="flex gap-2" data-testid="partner-priority-group">
                {(config.form_schema?.priorities || ['urgent', 'priority', 'routine']).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, priority: p }))}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${form.priority === p ? 'text-white border-transparent' : 'text-cn-text border-cn-border bg-cn-bg hover:border-cn-primary/40'}`}
                    style={form.priority === p ? { background: accent } : {}}
                    data-testid={`partner-priority-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-notes">Clinical notes (optional)</label>
              <textarea id="partner-notes" className="cn-input w-full" rows={4} value={form.notes} onChange={update('notes')} data-testid="partner-notes" />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="cn-btn cn-btn-primary w-full"
              style={{ background: accent }}
              data-testid="partner-submit"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting…' : 'Submit referral'}
            </Button>

            <p className="text-[11px] text-cn-text-muted leading-relaxed">
              By submitting, you confirm you have authority to share this patient&rsquo;s discharge
              context. CaraLoom routes the referral to verified care providers under an active BAA.
              No PHI is stored unencrypted at rest.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
